import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import {
  FaDownload,
  FaPrint,
  FaShieldAlt,
  FaCheckCircle,
  FaArrowLeft,
  FaBuilding,
  FaCalendarAlt,
  FaUserCheck,
  FaQrcode
} from 'react-icons/fa';
import requestsService from '../../Services/requests';
import logo from '../../assets/Images/LOGOS/Docity_Logo_updatedDocity_Text_Logo_White.png';
import logoEmblem from '../../assets/Images/LOGOS/Docity_Logo_updatedDocity_White_Emblem.png';
import fallbackLogo from '../../assets/Images/LOGOS/Docity_Logo.png';
import DocityBlackWordmark, { forceBlackCertificateLogo } from '../Common/DocityBlackWordmark';
import '../Soc/Soc.css';
import './CertificateDownloadHandler.css';

const normalizeResponseList = (response) => {
  if (!response || typeof response === 'string') return [];
  const raw = response?.data ?? response?.result ?? response;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return [raw];
  return [];
};

const getDirectCertificateUrl = (certificate = {}) => {
  const url =
    certificate?.downloadUrl ||
    certificate?.certificateDownloadUrl ||
    certificate?.pdfUrl ||
    certificate?.certificateUrl ||
    certificate?.fileUrl ||
    '';
  if (typeof url === 'string' && /^https?:\/\//i.test(url) && !url.includes('#/certificate-download') && !url.includes('blob:')) {
    return url;
  }
  return '';
};

const getSearchParams = (locationSearch) => {
  if (locationSearch && locationSearch.length > 1) {
    return new URLSearchParams(locationSearch);
  }
  const hash = window.location.hash || '';
  const qIndex = hash.indexOf('?');
  if (qIndex !== -1) {
    return new URLSearchParams(hash.substring(qIndex));
  }
  return new URLSearchParams(window.location.search);
};

const formatDisplayDate = (dateValue) => {
  if (!dateValue) return 'N/A';
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return String(dateValue);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return String(dateValue);
  }
};

const generateUniqueSealCode = (certificateId, ownerName, issuedDate) => {
  const source = `${certificateId || 'DOCITY'}|${ownerName || 'OWNER'}|${issuedDate || new Date().toISOString()}`;
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash << 5) - hash + source.charCodeAt(i);
    hash |= 0;
  }
  const encoded = Math.abs(hash).toString(36).toUpperCase().padStart(8, '0').slice(0, 8);
  return `DC-${encoded}`;
};

const CertificateDownloadHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const certificateRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [effectiveQrCode, setEffectiveQrCode] = useState('');
  const [directDownloadUrl, setDirectDownloadUrl] = useState('');

  const [certDetails, setCertDetails] = useState({
    type: 'soc',
    certificateId: '',
    ownerName: '',
    address: '',
    expiresAt: '',
    createdAt: '',
    isValid: true
  });

  // 1. Parse query params & fetch certificate data
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      setIsLoading(true);
      setErrorMessage('');

      const searchParams = getSearchParams(location.search);
      const type = (searchParams.get('type') || 'soc').toLowerCase();
      const certificateId =
        searchParams.get('id') ||
        searchParams.get('certificateId') ||
        searchParams.get('certId') ||
        '';

      const paramOwner = searchParams.get('name') || searchParams.get('owner') || searchParams.get('ownerName') || '';
      const paramAddr = searchParams.get('addr') || searchParams.get('address') || '';
      const paramExp = searchParams.get('exp') || searchParams.get('expiresAt') || '';
      const paramIss = searchParams.get('iss') || searchParams.get('issued') || searchParams.get('createdAt') || '';

      if (!certificateId) {
        if (isMounted) {
          setErrorMessage('Invalid certificate link. Missing certificate ID.');
          setIsLoading(false);
        }
        return;
      }

      // Initial state from URL parameters
      const initialCert = {
        type,
        certificateId,
        ownerName: paramOwner || 'Property Owner',
        address: paramAddr || 'Address submitted to DoCity',
        expiresAt: paramExp,
        createdAt: paramIss || new Date().toISOString(),
        isValid: true
      };

      if (isMounted) {
        setCertDetails(initialCert);
      }

      // Fetch from API in background to enrich or check direct download URL
      try {
        let response = null;
        if (type === 'soc') {
          response = await requestsService.getPublicSoc({ certificateId });
          if (!response || (Array.isArray(response) && response.length === 0)) {
            response = await requestsService.getsocs({ certificateId });
          }
        } else {
          response = await requestsService.getPublicOc({ OCId: certificateId });
          if (!response || (Array.isArray(response) && response.length === 0)) {
            response = await requestsService.getSingleOc({ OCId: certificateId });
          }
        }

        const list = normalizeResponseList(response);
        const fetched =
          list.find((c) => {
            const matchId = c?.certificateId || c?._id || c?.id || c?.SOCId || c?.OCId || c?.paymentId || c?.orderId;
            return String(matchId) === String(certificateId);
          }) || list[0];

        if (fetched && isMounted) {
          const directUrl = getDirectCertificateUrl(fetched);
          if (directUrl) {
            setDirectDownloadUrl(directUrl);
          }

          setCertDetails((prev) => ({
            ...prev,
            ownerName: fetched.ownerName || fetched.name || prev.ownerName,
            address: fetched.address || fetched.location || prev.address,
            expiresAt: fetched.expiresAt || prev.expiresAt,
            createdAt: fetched.createdAt || prev.createdAt,
            raw: fetched
          }));
        }
      } catch (e) {
        console.warn('[CertificateDownload] API enrichment notice:', e);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initialize();
    return () => {
      isMounted = false;
    };
  }, [location.search]);

  // 2. Generate QR code on the fly for the preview
  useEffect(() => {
    let isMounted = true;
    if (!certDetails.certificateId) return;

    const qrTarget = window.location.href;
    QRCode.toDataURL(qrTarget, {
      margin: 1,
      width: 256,
      color: { dark: '#000000', light: '#ffffff' }
    })
      .then((dataUrl) => {
        if (isMounted) setEffectiveQrCode(dataUrl);
      })
      .catch((err) => {
        console.error('[CertificateDownload] QR generation error:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [certDetails.certificateId]);

  // 3. Download PDF via html2canvas & jsPDF
  const downloadPdf = async () => {
    if (isDownloading) return;

    // If direct S3 download URL exists, trigger it directly
    if (directDownloadUrl) {
      const safeFileName = String(certDetails.certificateId || 'Certificate')
        .replace(/[^a-zA-Z0-9-_]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'Certificate';
      const link = document.createElement('a');
      link.href = directDownloadUrl;
      link.download = `${certDetails.type.toUpperCase()}-${safeFileName}.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloadSuccess(true);
      return;
    }

    const element = certificateRef.current;
    if (!element) return;

    setIsDownloading(true);
    setDownloadSuccess(false);

    try {
      if (document?.fonts?.ready) {
        await document.fonts.ready;
      }

      const safeFileName = String(certDetails.certificateId || 'Certificate')
        .replace(/[^a-zA-Z0-9-_]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'Certificate';

      const prefix = certDetails.type.toUpperCase();

      const canvas = await html2canvas(element, {
        scale: Math.max(2, window.devicePixelRatio || 1.5),
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0,
        logging: false,
        backgroundColor: '#d8e5e5',
        ignoreElements: (node) => node?.dataset?.html2canvasIgnore === 'true',
        onclone: (clonedDoc) => {
          clonedDoc.querySelectorAll('[data-export-hide="true"]').forEach((el) => {
            el.style.display = 'none';
          });
          forceBlackCertificateLogo(clonedDoc);
        }
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'px',
        format: [canvas.width, canvas.height],
        compress: true
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height, undefined, 'FAST');
      pdf.save(`${prefix}-${safeFileName}.pdf`);
      setDownloadSuccess(true);
    } catch (error) {
      console.error('[CertificateDownload] PDF generation error:', error);
      // Fallback: download as high-res PNG image
      try {
        const element = certificateRef.current;
        const canvas = await html2canvas(element, { scale: 2, useCORS: true, allowTaint: true });
        const imgData = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imgData;
        link.download = `${certDetails.type.toUpperCase()}-${certDetails.certificateId}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setDownloadSuccess(true);
      } catch (fallbackError) {
        console.error('[CertificateDownload] Fallback error:', fallbackError);
        alert('Unable to generate certificate download. Please try taking a screenshot or print.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // 4. Auto-trigger download once certificate is loaded and ready
  useEffect(() => {
    if (!isLoading && certDetails.certificateId && certificateRef.current && !errorMessage && !downloadSuccess) {
      const timer = setTimeout(() => {
        downloadPdf();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isLoading, certDetails.certificateId, errorMessage, directDownloadUrl, downloadSuccess]);

  const handlePrint = () => {
    window.print();
  };

  const isSoc = certDetails.type === 'soc';
  const certTitle = isSoc ? 'Self occupied Certificate' : 'Occupation Certificate';
  const sealCode = generateUniqueSealCode(certDetails.certificateId, certDetails.ownerName, certDetails.createdAt);

  if (isLoading) {
    return (
      <div className="cert-dl-page">
        <div className="cert-dl-loading-card">
          <img src={fallbackLogo} alt="DoCity Logo" className="cert-dl-logo-loading" />
          <div className="cert-dl-spinner" />
          <h3>Verifying Certificate...</h3>
          <p>Please wait while we retrieve and prepare your official certificate.</p>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="cert-dl-page">
        <div className="cert-dl-error-card">
          <img src={fallbackLogo} alt="DoCity Logo" className="cert-dl-logo-loading" />
          <div className="cert-dl-error-badge">Certificate Link Error</div>
          <h3>Unable to Load Certificate</h3>
          <p>{errorMessage}</p>
          <button className="cert-dl-btn-primary" onClick={() => navigate('/')}>
            <FaArrowLeft /> Go to DoCity Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cert-dl-page">
      {/* Top action header */}
      <div className="cert-dl-top-bar">
        <div className="cert-dl-bar-left">
          <img src={logo} alt="DoCity" className="cert-dl-nav-logo" onError={(e) => { e.target.src = fallbackLogo; }} />
          <div className="cert-dl-badge-verified">
            <FaCheckCircle className="cert-dl-badge-icon" />
            <span>Official Verified Certificate</span>
          </div>
        </div>

        <div className="cert-dl-bar-actions">
          <button
            className="cert-dl-btn-primary"
            onClick={downloadPdf}
            disabled={isDownloading}
            title="Download Certificate as PDF"
          >
            <FaDownload className={isDownloading ? 'cert-dl-spin-icon' : ''} />
            <span>{isDownloading ? 'Generating PDF...' : 'Download Certificate (PDF)'}</span>
          </button>
          <button className="cert-dl-btn-secondary" onClick={handlePrint} title="Print Certificate">
            <FaPrint />
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* Download Status Toast / Banner */}
      {downloadSuccess && (
        <div className="cert-dl-banner-success">
          <FaCheckCircle />
          <span>Certificate downloaded successfully! If your download did not start automatically, tap the button above.</span>
        </div>
      )}

      {/* Certificate Container (Rendered for export & preview) */}
      <div className="cert-dl-viewport">
        <div className="soc-oc-certificate cert-dl-target" ref={certificateRef}>
          <div className="soc-oc-header">
            <div className="soc-oc-top-actions" data-export-hide="true">
              <span className="cert-dl-id-tag">ID: {certDetails.certificateId}</span>
            </div>
            <div className="soc-header-text">
              <DocityBlackWordmark />
              <h3>{certTitle}</h3>
            </div>
          </div>

          <p className="soc-oc-declaration">
            <span className="soc-oc-declaration-line">This is to acknowledge and certify that</span>
            <span className="soc-oc-owner-highlight">{certDetails.ownerName || 'Property Owner'}</span>
            <span className="soc-oc-declaration-line">
              is the rightful occupier of the property submitted to DoCity Civil Engineers Private Limited.
            </span>
            <span className="soc-oc-declaration-line">
              The details and supporting documents provided for this application are accepted as true to the best of the applicant's knowledge.
            </span>
          </p>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0 16px 10px 16px', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <span className="soc-oc-label" style={{ display: 'block' }}>PROPERTY ADDRESS</span>
              <span style={{ fontSize: '0.75rem', wordBreak: 'break-word', color: '#0d1b2a', fontWeight: '500' }}>
                {certDetails.address || 'Address not available'}
              </span>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <span className="soc-oc-label" style={{ display: 'block' }}>CERTIFICATE EXPIRY</span>
              <span style={{ fontSize: '0.75rem', color: '#0d1b2a', fontWeight: '500' }}>
                {formatDisplayDate(certDetails.expiresAt)}
              </span>
            </div>
          </div>

          <div className="soc-oc-footer-grid">
            <div className="soc-oc-address-block">
              <div className="soc-oc-meta-col">
                <div className="soc-oc-qr-center soc-oc-qr-below-owner">
                  <div className="soc-oc-qr-frame">
                    {effectiveQrCode ? (
                      <img src={effectiveQrCode} className="soc-oc-qr" alt="QR Code" crossOrigin="anonymous" />
                    ) : (
                      <FaQrcode size={56} />
                    )}
                  </div>
                  <span className="soc-oc-qr-caption">Scan to verify certificate</span>
                </div>
              </div>
            </div>

            <div className="soc-oc-seal-wrap">
              <div className="soc-oc-seal" aria-label={`Unique seal ${sealCode}`}>
                <svg viewBox="0 0 200 200" className="soc-oc-seal-svg" role="img" aria-label="Docity company seal">
                  <defs>
                    <path id="dlSealTopArc" d="M 28 100 A 72 72 0 0 1 172 100" />
                    <path id="dlSealBottomArc" d="M 172 100 A 72 72 0 0 1 28 100" />
                  </defs>
                  <circle cx="100" cy="100" r="90" className="soc-seal-ring-outer" />
                  <circle cx="100" cy="100" r="62" className="soc-seal-ring-mid" />
                  <text className="soc-seal-text-arc soc-seal-text-top">
                    <textPath href="#dlSealTopArc" startOffset="50%" textAnchor="middle">
                      DOCITY.CO.IN
                    </textPath>
                  </text>
                  <text className="soc-seal-text-arc soc-seal-text-bottom">
                    <textPath href="#dlSealBottomArc" startOffset="50%" textAnchor="middle">
                      THE DOCITY SEAL
                    </textPath>
                  </text>
                  <text x="100" y="84" className="soc-seal-company" textAnchor="middle">
                    DOCITY SEAL
                  </text>
                  <image
                    href={logoEmblem}
                    x="82"
                    y="90"
                    width="36"
                    height="36"
                    preserveAspectRatio="xMidYMid meet"
                    className="soc-seal-logo-svg"
                  />
                </svg>
              </div>
            </div>

            <div className="soc-oc-sign-wrap">
              <strong
                style={{
                  fontFamily: "'Brush Script MT', 'Dancing Script', 'Georgia', cursive, serif",
                  fontSize: '26px',
                  fontWeight: '500',
                  fontStyle: 'italic',
                  color: '#0f4c81',
                  display: 'inline-block',
                  marginBottom: '2px',
                  whiteSpace: 'nowrap'
                }}
              >
                DoCity
              </strong>
              <div className="soc-oc-sign-line" />
              <span>Authorised Signature</span>
            </div>
          </div>

          <div className="disclaimer-text">
            <p className="dis-text">DoCity does not take responsibility for the title or possession of this property.</p>
          </div>
        </div>
      </div>

      {/* Floating Action for Mobile */}
      <div className="cert-dl-mobile-fab" data-export-hide="true">
        <button className="cert-dl-btn-primary cert-dl-fab-btn" onClick={downloadPdf} disabled={isDownloading}>
          <FaDownload className={isDownloading ? 'cert-dl-spin-icon' : ''} />
          <span>{isDownloading ? 'Generating...' : 'Download PDF'}</span>
        </button>
      </div>
    </div>
  );
};

export default CertificateDownloadHandler;
